import type * as React from 'react'
import type { TReduxAction } from '../../src/redux/actions'
import type { EStatus } from '../../src/utils/async'
import type { Form_IDisplayForm, Form_IForm } from '../Form/Form.types'
import type { NReports } from '../Rpts/Rpts.types'
import type { SD_ISmartDataText, SD_TSmartData } from '../SD/SD.types'
import type { Flow } from '../../../shared/domain/flows/types.no-deps'
import type { NominalUUID } from '../../../shared/lib/typeHelpers.no-deps'

export type {
    Workflow as Type_Workflow,
    AppActivityWorkflow as Type_ActivityWorkflow,
    AppWorkflowActivity as Type_IAction,
} from '../../../app-core/lib/workflow/Workflow'

export type Type_Diff<T extends string, U extends string> = ({ [P in T]: P } & { [P in U]: never } & {
    [x: string]: never
})[T]

export type Type_IndexedDbId = string | number

export type Type_TDispatch = (action: TReduxAction) => void

export interface Type_IIssue {
    id: number
    label: string
    element: string
    doc: string[]
    isFixable: boolean
}

export interface Type_ISmartDataOrdering {
    source: string
    rank: number
}

export interface Type_ISmartDataResult {
    title: string
    spec: Type_ISmartDataSpec[]
    data: SD_TSmartData[]
}

export interface Type_ISmartDataSpecGroup {
    title?: string
    spec: Type_ISmartDataSpec[]
}

export interface Type_ISmartDataSpec {
    id: number
    target: 'units' | 'locations'
}

export type Type_IReport = NReports.TDatabaseReport

export interface Type_IFix {
    fixId?: number
    title: string
    authorId: number
    editorId: number
    creationDate: string
    editionDate: string
    lastModified: string
    steps: never[]
}

export interface Type_IProcessedLocation extends Type_ILocation {
    address: string
    mapAddress?: string
    visible?: boolean
    distance?: number
}

export interface Type_ILocation {
    id: number
    locationId: number // copy of the id, needed for the db table key path
    name: string

    localId: string // this is the client's id

    type: string

    address1: string
    address2: string
    city: string
    state: string
    country: number
    postCode: string

    xCoord: number
    yCoord: number

    barcodeFormat?: string
}

export type Type_TTimelineEventReport = TTimelineEventBase & {
    id: number
    type: 'report'
    author: {
        name: string
    }
    item: {
        items: { length: number }
    }
}

export type Type_TTimelineEventReportUploading = TTimelineEventBase & {
    id: string
    type: 'report-uploading'
    author: {
        name: string
    }
    item: NReports.TIntermediateReport
}

export type Type_TTimelineEventText = TTimelineEventBase & {
    id: number | string
    type: 'stock' | 'text'
    item: string
    author: {
        name: string
    }
}

export type Type_TTimelineEventSmartData = TTimelineEventBase & {
    id: number
    type: 'smartdata'
    item: SD_ISmartDataText
}

type TTimelineEventBase = {
    date: string
    timeAgo?: string
    meta?: {
        new?: boolean
        redundant?: boolean
        localOnly?: boolean
    }
}

export type Type_TTimelineEvent =
    | Type_TTimelineEventReport
    | Type_TTimelineEventReportUploading
    | Type_TTimelineEventText
    | Type_TTimelineEventSmartData

// Warning this interface name may lead to confusion this should be named
// ILocationTimeline not to be confused with the events which it contains
export interface Type_ILocationTimelineEvent {
    id: number
    events: Type_TTimelineEvent[]
    status?: EStatus
}

export type Type_IProcessedCalendarEvent = Type_ICalendarEvent & {
    hasLocalReport: boolean
    hasOpenReport: boolean
}

export interface Type_ICalendarEvent {
    id: Type_IndexedDbId
    data: {}
    startDate: Date
    endDate: Date
    recurring: boolean
    location: number
    createdDate: Date
    author: number
    // complete is only true when the server knows about the report,
    // also check the hasLocalReport derived property
    complete: boolean
    onlineId?: number
    template?: number
    campaign?: number
    meta?: {
        new?: boolean
        updated?: boolean
        delete?: boolean
    }
}

export type Type_ProcessFlowType = 'units' | 'location'

export type Type_TReportMode = 'issue' | 'photo' | 'note'

export type Type_TCompletionType = 'allFixed'

type BaseActionType = {
    id: number
    name: string
    display: {
        color: string
        logo: string
    }
}

type BaseOptions = {
    manualBlock?: boolean
}

export type Type_SimpleActionType = BaseActionType & {
    type: 'simple'
    options: BaseOptions & {
        processFlow?: number
    }
}

export type Type_ReportActionType = BaseActionType & {
    type: 'report'
    options: Type_ReportOptionsWithSingleReportMode | Type_ReportOptionsWithMutltipleReportModes
    cameraResolution: 'low' | 'medium' | 'high'
}

export type Type_ReportBaseOptions = BaseOptions & {
    processFlow?: number
    unitListTypes: string[]
    targetUnitTypeId: number
    issues: number[]
    completion?: { type: Type_TCompletionType }
}

export type Type_ReportOptionsWithSingleReportMode = Type_ReportBaseOptions & {
    reportModes: [Type_TReportMode]
    selectUnitSingleClick: boolean
}

export type Type_ReportOptionsWithMutltipleReportModes = Type_ReportBaseOptions & {
    reportModes: Type_TReportMode[]
}

export function Type_hasSingleReportMode(
    options: Type_ReportOptionsWithSingleReportMode | Type_ReportOptionsWithMutltipleReportModes
): options is Type_ReportOptionsWithSingleReportMode {
    return options.reportModes.length === 1
}

export type Type_SimpleUnitActionType = BaseActionType & {
    type: 'simple-unit'
    report?: any
    options: BaseOptions & {
        unitListTypes: string[]
        targetUnitTypeId: number
        defaultAnswer?: Type_TAnswer
        processFlow: number
    }
}

export type Type_NoteActionType = BaseActionType & {
    type: 'note'
    note?: string
    options: BaseOptions & {
        // Needed in saveNoteToAnswer
        processFlow?: number
    }
}

export type Type_SendActionType = BaseActionType & {
    type: 'send'
    options: BaseOptions
}

export type Type_FormActionType = BaseActionType & {
    type: 'form'
    form?: Form_IForm[]
    options: BaseOptions & {
        formType: string
        generateTimelineNote?: boolean
    }
}

export type Type_UnitFormActionType = BaseActionType & {
    type: 'unit-form'
    options: BaseOptions & {
        formType: string
        unitListTypes: string[]
        targetUnitTypeId: number
    }
}

export type Type_ProcessFlowActionType = BaseActionType & {
    type: 'process-flow'
    options: BaseOptions & {
        processFlow: number
    }
}

export type Type_FlowActionType = BaseActionType & {
    type: 'flow'
    options: BaseOptions
    flow: Flow
}

export function Type_isFlowActionType(actionType: Type_IActionType): actionType is Type_FlowActionType {
    return actionType.type === 'flow'
}

export type Type_IActionType =
    | Type_SimpleActionType
    | Type_ReportActionType
    | Type_NoteActionType
    | Type_SendActionType
    | Type_FormActionType
    | Type_UnitFormActionType
    | Type_ProcessFlowActionType
    | Type_SimpleUnitActionType
    | Type_FlowActionType

export type Type_TIssueId = number

export type Type_TBooleanAnswer = {
    type: 'boolean'
    value: boolean
}

export type Type_TNumberAnswer = {
    type: 'number'
    value: number
}

export type Type_TChoiceAnswer = {
    type: 'choice'
    value: number[]
}

export type Type_TPhotoAnswer = {
    type: 'photo'
    value: number[] // Photo ids
}

export type Type_TTextAnswer = {
    type: 'text'
    value: string
}

export type Type_TFormAnswer = {
    type: 'form'
    value: Form_IForm[]
}

export type Type_TAnswer =
    | Type_TNumberAnswer
    | Type_TBooleanAnswer
    | Type_TChoiceAnswer
    | Type_TPhotoAnswer
    | Type_TTextAnswer
    | Type_TFormAnswer

export interface Type_IQuestionAnswer {
    question: number
    answer: Type_TAnswer
    finish?: boolean
}

export type Type_TFixId = number

export type Type_TNumericValue = {
    type: 'number'
    content: number
}

// Not included in TValue union as we want the code to be able to use it but to never actually store
// a null value in the reported issue data
export type Type_TNullValue = {
    type: 'null'
}

export type Type_TValue = Type_TNumericValue

export interface Type_IReportedIssueIssue {
    id: Type_TIssueId
    images: number[]
    note: string
    value?: Type_TValue
}

export interface Type_IReportedIssueFix {
    id: Type_TFixId
    images: number[]
}

export interface Type_IReportedIssue {
    issue: Type_IReportedIssueIssue
    fix?: Type_IReportedIssueFix
}

export type Type_DisplayUnitUuid = NominalUUID<'display-unit-uuid'>

export const Type_toDisplayUnitUuid = (id: string) => id as Type_DisplayUnitUuid

export interface Type_IDisplayUnit {
    instanceId: Type_DisplayUnitUuid
    unit: Type_IUnit
    reportedIssues: Type_IReportedIssue[]
    forms: Form_IForm[][]
    processFlow: Type_IQuestionAnswer[]
    unitListId?: number
    unitListItem?: Type_IUnitListItem
    infoPills: Type_IInfoPill[]
}

export interface Type_IInfoPill {
    icon?: string
    style: React.CSSProperties
    content: Array<{
        type: 'value' | 'text'
        value?: string
    }>
}

export type Type_TSectionType = 'ready' | 'stage' | 'finished'

export interface Type_IReportSection {
    label: string
    name: string
    display?: {
        color?: string
    }
    unitListId?: number
    info: string
    addUnits: boolean
    collapse: boolean
    type: Type_TSectionType
    sectionProducts: Type_DisplayUnitUuid[]
}

export interface Type_ITaskReportData {
    type: 'report'
    sections: Type_IReportSection[]
    units: Type_IDisplayUnit[]
    reportType: string
    storeId: number
}

export interface Type_ITaskProcessflowData {
    type: 'location-issue'
    processFlow: Type_IQuestionAnswer[]
    reportedIssues: Type_IReportedIssue[]
    forms: Form_IForm[][]
}

export interface Type_ITaskFormDataForm {
    form: number
    note?: string
    results: Array<{
        question: number
        answer: any
    }>
}

export interface Type_ITaskFormData {
    type: 'location-form'
    forms: Type_ITaskFormDataForm[]
}

export interface Type_ITaskUnitFormData {
    type: 'unit-form'
    displayUnits: Type_IDisplayUnit[]
    sections: Type_IReportSection[]
    displayForm: Form_IDisplayForm
}

export interface Type_ITaskReport {
    id: number
    date: Date
    report: Type_TTaskData
}

export type Type_TTaskData =
    | Type_ITaskReportData
    | Type_ITaskFormData
    | Type_ITaskProcessflowData
    | Type_ITaskUnitFormData

export interface Type_IUnit {
    id: number
    name: string
    type: number

    data: {
        [key: string]: any
    }
    foreignIds: {
        [key: string]: any
    }

    parents: number[]
    children: number[]
}

export interface Type_IUnitDisplayValuesFormat {
    data: Type_IDisplayValue[]
    foreignIds: Type_IDisplayValue[]
    unitListItem: Type_IDisplayValue[]
}
export interface Type_IDisplayValue {
    name?: string
    key: string
    ordering: number
    style?: object
    parent?: string
}

export interface Type_IUnitList {
    id: number
    name: string
    type: string
    unitType: number
    locations: number[]
    units: Type_IUnitListItem[]
    data: {
        info: any
        // TODO: addUnits & showAll are just ! of eachother, we should depreciate one of them
        addUnits: boolean
        addUnitSingleClick?: boolean
        addUnitDuplicates?: boolean
        showAll?: boolean
    }
}

export interface Type_IUnitListItem {
    id: number
    ordering?: number
    displayValues?: {
        [key: string]: any
    }
    endDate?: string
    startDate?: string
}

export interface Type_IFocusListLocation {
    id: number
    worth: number
    reports?: Array<{ id: number }>
    callQuantity: number
    displayValue?: number
}

export type Type_TProcessedFocusListLocation = Type_IProcessedLocation & Type_IFocusListLocation

export type Type_TStats = {
    toSchedule: number
    scheduled: number
    completed: number
}

export interface Type_ILocationFocusList {
    id?: number
    locationFocusListId: number
    name: string
    range: {
        startDate: string
        endDate: string
    }
    locations?: Type_IFocusListLocation[]
}

export interface Type_IRoutedUnitsSet {
    id?: number
    originActivity: number
    destinationActivity: number
    section: Array<{
        name: string
        units: Array<{
            id: number
        }>
    }>
}

export interface Type_IActivityOverwrite {
    id?: number
    origin: Type_TOverwriteOrigin
    activity: number
    field: 'completed' | 'blocked'
    value: boolean
}

interface IUnitsOrigin {
    type: 'units'
    data: number[]
}

interface ILocationOrigin {
    type: 'location'
    data: number
}

export type Type_TOverwriteOrigin = IUnitsOrigin | ILocationOrigin

export interface Type_IReportLocation {
    id: number
    name: string
    country: number
    barcodeFormat: string
    address: string
    distance: number
    mapAddress: string
    processFlow?: Type_IQuestionAnswer[]
    reportedIssues?: Type_IReportedIssue[]
    forms: Form_IForm[][]
}

export interface Type_IReportRestrictData {
    location?: number
    focusList?: number
    event?: number | string
}
